import { Button, ButtonColors } from '@chiroup/components/Button';
import { AdminTestPatientEnsureRequest } from '@chiroup/core/types/AdminTestPatient.type';
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import testPatientService from '../../../services/testPatient.service';

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : 'The request failed. Please try again.';

const ClinicTestPatient: React.FC<{ clinicId: number }> = ({ clinicId }) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  const [selected, setSelected] = useState<number[]>([]);
  const [downloadError, setDownloadError] = useState('');
  const [downloading, setDownloading] = useState<number | null>(null);
  const queryKey = ['clinicTestPatient', clinicId];
  const status = useQuery(queryKey, () => testPatientService.status(clinicId), {
    refetchOnWindowFocus: false,
  });
  const conditions = useQuery(
    ['clinicTestConditions', clinicId],
    () => testPatientService.conditions(clinicId),
    { refetchOnWindowFocus: false }
  );
  const ensure = useMutation(
    (body: AdminTestPatientEnsureRequest) => testPatientService.ensure(clinicId, body),
    {
      onSuccess: data => {
        queryClient.setQueryData(queryKey, data);
      },
    }
  );
  const createReport = useMutation(
    () => testPatientService.createReport(clinicId, { conditionIds: selected, requestId }),
    {
      onSuccess: () => {
        setRequestId(crypto.randomUUID());
        setSelected([]);
        setSearch('');
        status.refetch();
      },
      onError: () => {
        status.refetch();
      },
    }
  );
  const patient = status.data?.patient;
  const busy = ensure.isLoading || createReport.isLoading;
  const download = async (reportId: number) => {
    setDownloading(reportId);
    setDownloadError('');
    try {
      const result = await testPatientService.pdf(clinicId, reportId);
      const bytes = Uint8Array.from(atob(result.base64), character => character.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = result.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (error) {
      setDownloadError(errorMessage(error));
    } finally {
      setDownloading(null);
    }
  };
  const filtered = (conditions.data || []).filter(condition =>
    condition.name.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div className="space-y-6 p-6">
      <section className="rounded-lg border border-border bg-card p-6 text-card-foreground">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Test patient</h2>
          <Button
            text="Refresh"
            color={ButtonColors.plainWithBorder}
            disabled={busy || status.isFetching}
            onClick={() => status.refetch()}
          />
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Manage this clinic’s sample patient and create condition reports with the standard
          condition defaults. Reports are not emailed.
        </p>
        {status.isLoading && (
          <p className="mt-4" role="status">
            Loading test patient…
          </p>
        )}
        {status.isError && (
          <p className="mt-4 text-red-600 dark:text-red-400" role="alert">
            Unable to load the test patient. {errorMessage(status.error)}
          </p>
        )}
        {status.data && (
          <div className="mt-4 space-y-3">
            <p>
              Status:{' '}
              <strong>
                {patient
                  ? patient.disabled
                    ? 'Deleted (inactive)'
                    : 'Active'
                  : status.data.legacyCandidates.length
                    ? 'Needs confirmation'
                    : 'Not found'}
              </strong>
            </p>
            {patient && (
              <>
                <p className="break-all text-sm">
                  Patient ID: <code>{patient.ID}</code>
                </p>
                <a
                  className="block break-all text-sm text-primary-700 underline dark:text-primary-400"
                  href={patient.patientUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {patient.patientUrl}
                </a>
                <p className="text-xs text-muted-foreground">
                  Patient and report links require ChiroUp access with this clinic selected.
                </p>
                {patient.disabled && (
                  <Button
                    text="Reactivate test patient"
                    loading={ensure.isLoading}
                    disabled={busy}
                    onClick={() => ensure.mutate({})}
                  />
                )}
              </>
            )}
            {!patient && !status.data.legacyCandidates.length && (
              <Button
                text="Create test patient"
                loading={ensure.isLoading}
                disabled={busy}
                onClick={() => ensure.mutate({})}
              />
            )}
            {!patient && status.data.legacyCandidates.length > 0 && (
              <>
                <p className="text-sm">
                  Existing patients are named “Sample Patient” but have no test-patient marker.
                  Confirm which record is a demo patient before using it. Confirmation also
                  reactivates that patient.
                </p>
                {status.data.legacyCandidates.map(candidate => (
                  <div
                    key={candidate.ID}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm">
                        {candidate.disabled ? 'Deleted (inactive)' : 'Active'}
                      </p>
                      <a
                        className="break-all text-sm text-primary-700 underline dark:text-primary-400"
                        href={candidate.patientUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {candidate.ID}
                      </a>
                    </div>
                    <Button
                      text="Confirm as test patient"
                      disabled={busy}
                      loading={ensure.isLoading}
                      onClick={() => ensure.mutate({ legacyPatientId: candidate.ID })}
                    />
                  </div>
                ))}
              </>
            )}
          </div>
        )}
        {ensure.isError && (
          <p className="mt-3 text-red-600 dark:text-red-400" role="alert">
            {errorMessage(ensure.error)}
          </p>
        )}
      </section>
      <section className="rounded-lg border border-border bg-card p-6 text-card-foreground">
        <h2 className="text-lg font-semibold">Create a condition report</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Choose up to 20 conditions for one combined report. Common treatments, advice, and
          exercises are added automatically. You can create additional reports afterward.
        </p>
        {!patient || patient.disabled ? (
          <p className="mt-4 text-sm">Activate a test patient to create reports.</p>
        ) : (
          <>
            <label className="mt-4 block text-sm font-medium" htmlFor="test-condition-search">
              Find conditions
            </label>
            <input
              id="test-condition-search"
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder="Search by condition name"
              className="mt-1 w-full rounded-md border-input bg-background text-foreground"
              disabled={busy}
            />
            {conditions.isLoading && <p role="status">Loading conditions…</p>}
            {conditions.isError && (
              <div role="alert">
                <p>Unable to load conditions.</p>
                <Button text="Retry conditions" onClick={() => conditions.refetch()} />
              </div>
            )}
            <div
              className="mt-3 max-h-72 overflow-y-auto rounded-md border border-border p-2"
              aria-label="Conditions"
            >
              {filtered.map(condition => (
                <label
                  key={condition.ID}
                  className="flex cursor-pointer items-center gap-3 rounded p-2 hover:bg-muted"
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(condition.ID)}
                    disabled={busy || (!selected.includes(condition.ID) && selected.length >= 20)}
                    onChange={event => {
                      setRequestId(crypto.randomUUID());
                      setSelected(previous =>
                        event.target.checked
                          ? [...previous, condition.ID]
                          : previous.filter(id => id !== condition.ID)
                      );
                    }}
                  />
                  <span className="text-sm">{condition.name}</span>
                </label>
              ))}
              {conditions.isSuccess && !filtered.length && (
                <p className="p-2 text-sm text-muted-foreground">No matching conditions.</p>
              )}
            </div>
            <p className="my-3 text-sm" aria-live="polite">
              {selected.length} selected
              {selected.length
                ? `: ${(conditions.data || [])
                    .filter(condition => selected.includes(condition.ID))
                    .map(condition => condition.name)
                    .join(', ')}`
                : ''}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button
                text="Create report"
                loading={createReport.isLoading}
                disabled={busy || !selected.length || conditions.isError}
                onClick={() => createReport.mutate()}
              />
              <Button
                text="Clear selection"
                color={ButtonColors.plainWithBorder}
                disabled={busy || !selected.length}
                onClick={() => {
                  setRequestId(crypto.randomUUID());
                  setSelected([]);
                }}
              />
            </div>
          </>
        )}
        {createReport.isError && (
          <p className="mt-3 text-red-600 dark:text-red-400" role="alert">
            {errorMessage(createReport.error)} Check the report list before retrying.
          </p>
        )}
        {createReport.isSuccess && (
          <p className="mt-3 text-green-700 dark:text-green-400" role="status">
            Report {createReport.data.ID} created.{' '}
            <a
              className="underline"
              href={createReport.data.reportUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open report
            </a>{' '}
            ·{' '}
            <button
              className="underline"
              disabled={downloading !== null}
              onClick={() => download(createReport.data.ID)}
            >
              Download PDF
            </button>
          </p>
        )}
      </section>
      <section className="rounded-lg border border-border bg-card p-6 text-card-foreground">
        <h2 className="text-lg font-semibold">Recent reports</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Latest 50 reports for this test patient.
        </p>
        {downloadError && (
          <p role="alert" className="mt-3 text-red-600 dark:text-red-400">
            PDF download failed: {downloadError}
          </p>
        )}
        {downloading !== null && (
          <p role="status" className="mt-3 text-sm">
            Preparing PDF for report {downloading}…
          </p>
        )}
        {!status.data?.reports.length && (
          <p className="mt-4 text-sm text-muted-foreground">No reports to display.</p>
        )}
        <ul className="mt-3 divide-y divide-border">
          {status.data?.reports.map(report => (
            <li
              key={report.ID}
              className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start sm:justify-between"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium">{report.conditions || 'Condition report'}</p>
                <p className="text-xs text-muted-foreground">
                  Report {report.ID} · {String(report.createdAt).slice(0, 10)}
                </p>
                <a
                  className="mt-1 block break-all text-sm text-primary-700 underline dark:text-primary-400"
                  href={report.reportUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {report.reportUrl}
                </a>
              </div>
              <Button
                text="Download PDF"
                color={ButtonColors.plainWithBorder}
                loading={downloading === report.ID}
                disabled={downloading !== null}
                onClick={() => download(report.ID)}
              />
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};
export default ClinicTestPatient;
